import { inject, Injectable } from '@angular/core';
import { ChartThemeService } from '@sb/ui';
import { ChartData, ChartDataset, ChartOptions, ChartType } from 'chart.js';

import { GraphSize } from '../enums';
import { ChartScheduleResponse, ChartTurnoverResponse } from '../models';

export type ChartDataLine = ChartData<'line'>;

export interface ChartLineChart {
  data: ChartData<'line'>;
  type: ChartType;
  options: ChartOptions<'line'>;
}

@Injectable({
  providedIn: 'root',
})
export class ChartHelperService {
  private readonly chartTheme = inject(ChartThemeService);

  public lineChartOptions: ChartLineChart['options'] = {
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 20,
          usePointStyle: false,
          pointStyle: 'rectRounded',
        },
      },
      tooltip: {
        position: 'nearest',
      },
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  private readonly heightMap: Record<GraphSize, string> = {
    small: '270px',
    medium: '400px',
  };

  public getHeight(size: GraphSize): string {
    return this.heightMap[size];
  }

  public convertResponseToLineChart(
    chartResponse: ChartScheduleResponse | ChartTurnoverResponse,
    groupName: string,
    propertyName: string,
  ): ChartLineChart {
    const data = this.mapResponseToLineChartData(chartResponse, groupName, propertyName);
    return {
      data,
      type: 'line',
      options: this.lineChartOptions,
    };
  }

  private mapResponseToLineChartData(
    chartResponse: ChartScheduleResponse | ChartTurnoverResponse,
    groupName: string,
    propertyName: string,
  ): ChartDataLine {
    const group = chartResponse[groupName];
    const keys = Object.keys(group);
    const colors = this.chartTheme.getColorsByDataSize(keys.length);

    const data: ChartDataset<'line'>[] = keys.map((key, index) => ({
      label: key.replace(/\([0-9]*?\)/g, ''),
      data: group[key].map((item) => item[propertyName]),
      borderColor: colors[index],
      backgroundColor: colors[index],
      tension: 0.2,
      cubicInterpolationMode: 'monotone',
      borderWidth: 3,
      pointBorderColor: colors[index],
      pointBackgroundColor: colors[index],
      pointHoverBackgroundColor: '#ffffff',
      pointHoverBorderColor: colors[index],
      pointHoverRadius: 8,
      pointHoverBorderWidth: 3,
      pointHitDetectionRadius: 35,
    }));

    return {
      labels: chartResponse.labels,
      datasets: data,
    };
  }
}
